import { z } from "zod";

/**
 * The company's saved outreach instructions (PREC-3687 §5, D12).
 *
 * Its own module rather than `schema/list.ts`: that file is at the length ratchet ceiling and may
 * only shrink. Follows `list-outreach-config.schema.ts`.
 *
 * One table, two libraries: `COPYWRITING` templates are scoped to a list source type and
 * `SENDER_ROUTING` templates are company-wide (`listSourceType === null`). The API rejects any
 * other combination with a named reason, so the frontend never has to guess a scope.
 */
export const OutreachTemplateKindSchema = z.enum(["COPYWRITING", "SENDER_ROUTING"]);

export type OutreachTemplateKind = z.infer<typeof OutreachTemplateKindSchema>;

export const OutreachInstructionTemplateSchema = z.object({
	id: z.number(),
	kind: OutreachTemplateKindSchema,
	/** The source type a COPYWRITING template serves; `null` for company-wide sender rules. */
	listSourceType: z.string().nullable(),
	name: z.string(),
	/** The instruction text — copied into the list's config when the template is loaded. */
	body: z.string(),
	/** The company default for this scope; new lists of that source type prefill from it. */
	isDefault: z.boolean(),
	/** Provenance only — every user in the company may edit any template. */
	createdByUserId: z.number().nullable(),
});

export type OutreachInstructionTemplate = z.infer<typeof OutreachInstructionTemplateSchema>;

export const OutreachInstructionTemplateListSchema = z.array(OutreachInstructionTemplateSchema);

/** The POST body. `listSourceType` is required for COPYWRITING and omitted for SENDER_ROUTING. */
export const CreateOutreachInstructionTemplateSchema = z.object({
	kind: OutreachTemplateKindSchema,
	listSourceType: z.string().optional(),
	name: z.string(),
	body: z.string(),
	isDefault: z.boolean().optional(),
});

export type CreateOutreachInstructionTemplate = z.infer<
	typeof CreateOutreachInstructionTemplateSchema
>;

/** The PATCH body. The scope columns are not editable — that would be a new template. */
export const UpdateOutreachInstructionTemplateSchema = z.object({
	name: z.string().optional(),
	body: z.string().optional(),
	isDefault: z.boolean().optional(),
});

export type UpdateOutreachInstructionTemplate = z.infer<
	typeof UpdateOutreachInstructionTemplateSchema
>;
